import _ from 'lodash';
import conrad from '@ibiquity/dashboard.conrad';
import { getCustomMarket, saveCustomMarket } from '@/services/market';

const state = {
  loading: false,
  id: undefined,
  name: '',
  broadcasts: null,
  sortField: null,
  sortOrder: null,
  excludedBroadcastTypes: ['AM'],
  preselectedBroadcastIds: [],
  location: null,
};

/* eslint-disable no-param-reassign, no-shadow */
const getters = {
  initialized(state) {
    return state.broadcasts !== null;
  },
  orderedBroadcasts(state, getters) {
    const { sortField, sortOrder } = state;
    if (!sortField) return state.broadcasts;
    if (sortField === 'type') {
      return _.orderBy(state.broadcasts, getters.broadcastType, [sortOrder]);
    }
    return _.orderBy(state.broadcasts, broadcast => broadcast[sortField] || '', [sortOrder]);
  },
  filteredBroadcasts(state, getters) {
    const { orderedBroadcasts } = getters;
    const { excludedBroadcastTypes } = state;
    const excludedBands = ['AM', 'DAB'].filter(band => _.includes(excludedBroadcastTypes, band));
    const isFmExcluded = _.includes(excludedBroadcastTypes, 'FM');
    const isHdExcluded = _.includes(excludedBroadcastTypes, 'HD');

    return _.filter(orderedBroadcasts, ({ band, type }) => {
      if (_.includes(excludedBands, band)) return false;
      if (isFmExcluded && band === 'FM' && type !== 'hdradio') return false;
      if (isHdExcluded && type === 'hdradio') return false;
      return true;
    });
  },
  selectedBroadcasts(state, getters) {
    const { filteredBroadcasts } = getters;
    const { preselectedBroadcastIds } = state;
    return _.filter(filteredBroadcasts, ({ id }) => _.includes(preselectedBroadcastIds, id));
  },
  isSelected(state, getters) {
    return broadcastId => !!getters.selectedBroadcasts.find(({ id }) => broadcastId === id);
  },
  isEveryFilteredBroadcastSelected(state, getters) {
    const { filteredBroadcasts, selectedBroadcasts } = getters;

    if (_.isEmpty(filteredBroadcasts)) return false;
    return filteredBroadcasts.length === selectedBroadcasts.length;
  },
  availableBroadcastTypes(state) {
    const { broadcasts } = state;
    const broadcastTypes = {
      AM: _.some(broadcasts, { band: 'AM' }),
      DAB: _.some(broadcasts, { band: 'DAB' }),
      FM: _.some(broadcasts, ({ band, type }) => band === 'FM' && type !== 'hdradio'),
      HD: _.some(broadcasts, ({ band, type }) => band === 'FM' && type === 'hdradio'),
    };
    return Object.keys(_.pickBy(broadcastTypes));
  },
  isBroadcastTypeExcluded(state) {
    return type => _.includes(state.excludedBroadcastTypes, type);
  },
  isBroadcastTypeAvailable(state, getters) {
    return type => _.includes(getters.availableBroadcastTypes, type);
  },
  canSaveCustomMarket(state, getters) {
    return getters.selectedBroadcasts.length >= 8;
  },
  broadcastType() {
    return ({ band, type }) => (type === 'hdradio' ? 'HD' : band);
  },
  isNameSpecified(state) {
    return state.name && state.name !== 'Custom Market';
  },
};

const actions = {
  reset({ commit }, { broadcasts = null, location = null } = {}) {
    commit('clearBroadcastSelections');
    commit('resetExcludedBroadcastTypes');
    commit('setBroadcasts', broadcasts);
    commit('setLocation', location);
    commit('setLoading', false);
  },

  loadBroadcasts({ commit, dispatch }, location) {
    commit('setLoading', true);
    commit('setLocation', location);

    const { longitude: lng, latitude: lat } = location;
    return conrad.getBroadcasts({ lat, lng })
      .then(({ data }) => {
        dispatch('reset', { broadcasts: data, location });
      })
      .catch((error) => {
        dispatch('reset');
        throw error;
      });
  },

  saveCustomMarket({
    commit, state, getters,
  }) {
    if (!getters.canSaveCustomMarket) return null;
    if (!state.id) commit('setId', Math.random().toString(36).substr(2, 9));
    const customMarket = {
      id: state.id,
      name: state.name,
      location: state.location,
      broadcasts: getters.selectedBroadcasts,
    };
    saveCustomMarket(customMarket);
    return customMarket;
  },

  async loadCustomMarket({ commit, dispatch }, marketId) {
    const customMarket = await getCustomMarket(marketId);
    if (customMarket) {
      const {
        id, name, location, broadcasts,
      } = customMarket;
      commit('setId', id);
      commit('setName', name);
      commit('setLocation', location);
      await dispatch('loadBroadcasts', location);
      commit('selectBroadcasts', _.map(broadcasts, 'id'));
    } else {
      await dispatch('reset');
      commit('setId', null);
      commit('setName', 'Custom Market');
    }
  },
};

const mutations = {
  setId(state, id) {
    state.id = id;
  },
  setName(state, name) {
    state.name = name;
  },
  setLoading(state, loading) {
    state.loading = loading;
  },
  setBroadcasts(state, broadcasts) {
    state.broadcasts = broadcasts;
  },
  setLocation(state, location) {
    state.location = location;
  },
  setSortField(state, field) {
    state.sortField = field;
    state.sortOrder = 'asc';
  },
  setSortOrder(state, order) {
    if (!_.includes(['asc', 'desc'], order)) {
      throw new Error(`Unknwon order ${order}`);
    }
    state.sortOrder = order;
  },
  selectBroadcast(state, broadcastId) {
    if (!state.broadcasts.find(broadcast => broadcastId === broadcast.id)) return;
    state.preselectedBroadcastIds = _.uniq([...state.preselectedBroadcastIds, broadcastId]);
  },
  unselectBroadcast(state, broadcastId) {
    const index = state.preselectedBroadcastIds.findIndex(id => id === broadcastId);
    if (index === -1) return;
    state.preselectedBroadcastIds.splice(index, 1);
  },
  selectBroadcasts(state, broadcastIds) {
    const allBroadcastIds = _.map(state.broadcasts, 'id');
    state.preselectedBroadcastIds = _.uniq([
      ...state.preselectedBroadcastIds,
      ..._.filter(broadcastIds, id => _.includes(allBroadcastIds, id)),
    ]);
  },
  unselectBroadcasts(state, broadcastIds) {
    state.preselectedBroadcastIds = _.difference(state.preselectedBroadcastIds, broadcastIds);
  },
  clearBroadcastSelections(state) {
    state.preselectedBroadcastIds = [];
  },
  resetExcludedBroadcastTypes(state) {
    state.excludedBroadcastTypes = ['AM'];
  },
  toggleBroadcastTypeFilter(state, type) {
    const index = state.excludedBroadcastTypes.findIndex(excludedType => excludedType === type);
    if (index === -1) {
      state.excludedBroadcastTypes = _.uniq([...state.excludedBroadcastTypes, type]);
    } else {
      state.excludedBroadcastTypes.splice(index, 1);
    }
  },
};
/* eslint-enable no-param-reassign, no-shadow */

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
